import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import supabaseService from '../services/supabaseService';

export const useDomains = () => {
  const { user, isAdmin } = useAuth();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load domains
  const loadDomains = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      const userDomains = await supabaseService.loadDomains();
      console.log('🏠 useDomains hook loaded domains:', userDomains);
      setDomains(userDomains);
    } catch (err) {
      console.error('Failed to load domains:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  // Create domain
  const createDomain = useCallback(async (domainData) => {
    if (!user) throw new Error('User not authenticated');

    try {
      setLoading(true);
      setError(null);
      
      const newDomain = await supabaseService.createDomain(domainData, user.id);
      setDomains(prev => [...prev, newDomain]);
      
      return newDomain;
    } catch (err) {
      console.error('Failed to create domain:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Update domain
  const updateDomain = useCallback(async (domainId, updates) => {
    if (!user) throw new Error('User not authenticated');

    try {
      setLoading(true);
      setError(null);
      
      const updatedDomain = await supabaseService.updateDomain(domainId, updates);
      
      setDomains(prev => 
        prev.map(domain => 
          domain.id === domainId ? updatedDomain : domain
        )
      );
      
      return updatedDomain;
    } catch (err) {
      console.error('Failed to update domain:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  // Delete domain
  const deleteDomain = useCallback(async (domainId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      setLoading(true);
      setError(null);
      
      console.log('🗑️ Deleting domain:', domainId);

      // Delete from database
      await supabaseService.deleteDomain(domainId);

      // Remove from local state
      setDomains(prev => prev.filter(domain => domain.id !== domainId));

      console.log('✅ Domain deleted successfully');
      
      return true;
    } catch (err) {
      console.error('Failed to delete domain:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  // Verify domain
  const verifyDomain = useCallback(async (domainId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      setLoading(true);
      setError(null);
      
      const result = await supabaseService.verifyDomain({ id: domainId });
      
      if (result.verified) {
        setDomains(prev => 
          prev.map(domain => 
            domain.id === domainId ? result.domain : domain
          )
        );
      }
      
      return result;
    } catch (err) {
      console.error('Failed to verify domain:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Force verify domain (for demo/admin)
  const forceVerifyDomain = useCallback(async (domainId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      setLoading(true);
      setError(null);
      
      const updatedDomain = await supabaseService.updateDomain(domainId, { isVerified: true });
      
      setDomains(prev => 
        prev.map(domain => 
          domain.id === domainId ? updatedDomain : domain
        )
      );
      
      return updatedDomain;
    } catch (err) {
      console.error('Failed to force verify domain:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  // Get domain by ID
  const getDomainById = useCallback((domainId) => {
    return domains.find(domain => domain.id === domainId);
  }, [domains]);

  // Search domains
  const searchDomains = useCallback(async (query) => {
    if (!user) return [];

    try {
      setError(null);
      const results = await supabaseService.loadDomains();
      return results;
    } catch (err) {
      console.error('Failed to search domains:', err);
      setError(err.message);
      return [];
    }
  }, [user, isAdmin]);

  // Get verification instructions
  const getVerificationInstructions = useCallback((domain) => {
    return supabaseService.getDNSInstructions(domain.domain, domain.verificationToken);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load domains on mount and when user changes
  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  return {
    domains,
    loading,
    error,
    createDomain,
    updateDomain,
    deleteDomain,
    verifyDomain,
    forceVerifyDomain,
    getDomainById,
    searchDomains,
    getVerificationInstructions,
    loadDomains,
    clearError
  };
};